import React from 'react';

import { Stack, Text, cn } from '@eluve/components';
import { SupportedLanguages } from '@eluve/language-utils';

export interface BundleDefaultListItem {
  id: string;
  bundleDisplayName: string;
  specialty: string | null;
  language: string | null;
}

interface BundleDefaultsListProps {
  rows: BundleDefaultListItem[];
  selectedId: string | null;
  onSelect: (row: BundleDefaultListItem) => void;
  emptyMessage?: string;
}

const languageLabel = (code: string | null): string => {
  if (!code) return 'Any';
  const match = Object.entries(SupportedLanguages).find(([, c]) => c === code);
  return match ? match[0] : code;
};

export const BundleDefaultsList: React.FC<BundleDefaultsListProps> = ({
  rows,
  selectedId,
  onSelect,
  emptyMessage = 'No defaults configured',
}) => {
  if (rows.length === 0) {
    return (
      <Stack className="px-3 py-4" gap={2}>
        <Text size="xs" color="tertiary">
          {emptyMessage}
        </Text>
      </Stack>
    );
  }

  return (
    <Stack align="stretch">
      {rows.map((row) => {
        const isSelected = selectedId === row.id;
        return (
          <button
            key={row.id}
            type="button"
            onClick={() => onSelect(row)}
            className={cn(
              'cursor-pointer border-b border-border-tertiary px-3 py-2.5 text-left last:border-b-0',
              isSelected
                ? 'bg-bg-accent-strong-primary text-white'
                : 'hover:bg-bg-tertiary',
            )}
            aria-pressed={isSelected}
            aria-label={`Select default: ${row.bundleDisplayName}`}
          >
            <Stack gap={0} align="start">
              <Text
                inline
                size="xs"
                weight={isSelected ? 'semibold' : 'medium'}
                className={cn(
                  'line-clamp-2',
                  isSelected ? 'text-white' : undefined,
                )}
              >
                {row.bundleDisplayName}
              </Text>
              <Text
                inline
                size="xs"
                color={isSelected ? undefined : 'tertiary'}
                className={isSelected ? 'text-white/80' : undefined}
              >
                {row.specialty ?? 'Any specialty'} ·{' '}
                {languageLabel(row.language)}
              </Text>
            </Stack>
          </button>
        );
      })}
    </Stack>
  );
};
